import { Box, Td, Tr } from '@chakra-ui/react';
import { getIn } from 'formik';
import * as _ from 'lodash';
import React, { useState } from 'react';

import { getPropertyShell } from '@finley-technologies/credit-agreement-utils';

import { WipConfig } from '../../types';
import { checkHasNoEffect } from '../utils';
import { NoEffectTooltip } from './NoEffectTooltip';
import {
  AddIconButton,
  EditIconButton,
} from 'packages/common-frontend/components/IconButton';
import {
  CreditAgreement,
  getBooleanSymbol,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { DeletePropertyButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeletePropertyButton';
import { EditPropertyContainerModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditPropertyContainerModal';

type BasePropertyContainerParams = {
  propertyKey: string;
  wipConfig?: WipConfig;
  isOptional: boolean;
  basePropertyKey: string;
  fullPropertyKey: string;
};

type PropertyContainerDetailProps = {
  basePropertyContainerParams: BasePropertyContainerParams;
  propertyContainer: CreditAgreement.DomainEntity.PropertyContainer;
  headers: (
    | keyof CreditAgreement.DomainEntity.PropertyContainer
    | keyof CreditAgreement.DomainEntity.Property
  )[];
  columnWidth?: number;
  Dropdown?: React.ReactElement;
};
const PropertyContainerDetail = ({
  propertyContainer,
  basePropertyContainerParams: {
    basePropertyKey,
    propertyKey,
    wipConfig,
    fullPropertyKey,
  },
  headers,
  columnWidth,
  Dropdown,
}: PropertyContainerDetailProps) => {
  const [
    isEditPropertyContainerOpen,
    setIsEditPropertyContainerOpen,
  ] = useState(false);

  const {
    name,
    section,
    description,
    _isWrittenIn_,
    _note_,
  } = propertyContainer;

  const columnWidthStr = `${columnWidth}%`;
  const hasNoEffectLabel = checkHasNoEffect({ basePropertyKey, propertyKey });

  return (
    <>
      <Tr>
        <Td w={columnWidthStr}>
          <Box>{name}</Box>
          {hasNoEffectLabel ? <NoEffectTooltip /> : null}
        </Td>
        {_.includes(headers, 'value') && <Td w={columnWidthStr}>-</Td>}
        {_.includes(headers, 'section') && (
          <Td w={columnWidthStr}>{section || '-'}</Td>
        )}
        {_.includes(headers, 'description') && (
          <Td w={columnWidthStr}>{description || '-'}</Td>
        )}
        {_.includes(headers, '_note_') && (
          <Td w={columnWidthStr}>{_note_ || '-'}</Td>
        )}
        {_.includes(headers, '_isWrittenIn_') && (
          <Td w={columnWidthStr}>
            <>{getBooleanSymbol(_isWrittenIn_)}</>
          </Td>
        )}

        {wipConfig && wipConfig.isMutateAllowed(fullPropertyKey) && (
          <Td w={columnWidthStr}>
            <EditIconButton
              ariaLabel="edit"
              label="Edit"
              onClick={(event) => {
                setIsEditPropertyContainerOpen(true);
                if (event) {
                  event.stopPropagation();
                }
              }}
            />
            <EditPropertyContainerModal
              setIsOpen={setIsEditPropertyContainerOpen}
              isOpen={isEditPropertyContainerOpen}
              fullPropertyKey={fullPropertyKey}
              wipConfig={wipConfig}
            />
            <AddIconButton
              ariaLabel="add"
              label="Add"
              onClick={(event) => {
                if (event) {
                  event.stopPropagation();
                }
                const propertyType =
                  wipConfig.propertyTypeContainerToPropertyMap[
                    propertyContainer.type
                  ];
                const propertyShell = getPropertyShell({
                  type: propertyType,
                  propertyKey,
                });
                const _contents = getIn(
                  wipConfig.formProps.values,
                  `${fullPropertyKey}.contents`,
                );
                const updatedContents = [..._contents, propertyShell];
                wipConfig.formProps.setFieldValue(
                  `${fullPropertyKey}.contents`,
                  updatedContents,
                );
              }}
            />
            {!getIn(wipConfig.wipShell, fullPropertyKey) && (
              <DeletePropertyButton
                formProps={wipConfig.formProps}
                basePropertyKey={basePropertyKey}
                propertyKey={propertyKey}
              />
            )}
          </Td>
        )}
        {Dropdown || <Td w="5%" />}
      </Tr>
    </>
  );
};

type PropertyContainerProps = {
  basePropertyKey: string;
  property: CreditAgreement.DomainEntity.PropertyContainer;
  propertyKey: string;
  wipConfig?: WipConfig;
  isOptional: boolean;
  headers: (
    | keyof CreditAgreement.DomainEntity.PropertyContainer
    | keyof CreditAgreement.DomainEntity.Property
  )[];
  columnWidth?: number;
  Dropdown?: React.ReactElement;
};
export const PropertyContainer = ({
  property,
  propertyKey,
  wipConfig,
  basePropertyKey,
  isOptional,
  headers,
  columnWidth,
  Dropdown,
}: PropertyContainerProps) => {
  const fullPropertyKey = `${basePropertyKey}.${propertyKey}`;

  const basePropertyContainerParams: BasePropertyContainerParams = {
    propertyKey,
    wipConfig,
    isOptional,
    basePropertyKey,
    fullPropertyKey,
  };

  return (
    <PropertyContainerDetail
      propertyContainer={property}
      basePropertyContainerParams={basePropertyContainerParams}
      headers={headers}
      columnWidth={columnWidth}
      Dropdown={Dropdown}
    />
  );
};
